import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Prompt, useHistory, useParams } from 'react-router-dom'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  makeStyles,
  Snackbar
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import SydButton from '../../../../../commonDesign/Button'
import SydInput from '../../../../../commonDesign/SydInput'
import SydLabel from '../../../../../commonDesign/SydLabel'
import Loading from '../../../../../molecules/Loading'
import ErrorDisplay from '../../../../../molecules/ErrorDisplay'
import {
  useAddQueryBlueprintDefinition,
  useGetQueryBlueprint,
  useListQueryBlueprintVersions,
  useModifyQueryBlueprintDefinition,
  useRemoveQueryBlueprintVersion,
  useSetActiveQueryBlueprintVersion
} from '../../../../../../api/denali'
import { decipherError } from '../../../../../../utils/decipherError'
import AgentSidePanel from './AgentSidePanel'
import { BlueprintStudioProvider, useBlueprintStudio } from './BlueprintStudioContext'
import BlueprintMetadataModal from './BlueprintMetadataModal'
import DqlDiagnosticsModal from './Editor/DqlDiagnosticsModal'
import DraftBrowser from './Editor'
import PreviewModal from './Editor/PreviewModal'

const useStyles = makeStyles(() => ({
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 120px)',
    overflow: 'hidden'
  },
  headerBar: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 'var(--gap-md)',
    padding: 'var(--padding-sm) var(--padding-md)',
    margin: 'var(--margin-md) var(--margin-lg) 0',
    borderRadius: 'var(--border-radius-lg)',
    border: '1px solid var(--color-neutral-300)',
    backgroundColor: 'var(--color-neutral-100)',
    flexShrink: 0
  },
  headerField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  fieldLabel: {
    fontSize: 'var(--text-size-xs)',
    fontWeight: 'var(--text-weight-bold)',
    color: 'var(--color-neutral-500)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    paddingLeft: '2px'
  },
  fieldInput: {
    padding: '6px 10px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-sm)',
    outline: 'none',
    backgroundColor: 'white',
    color: 'var(--color-neutral-900)',
    '&:focus': {
      borderColor: 'var(--color-primary-400)',
      boxShadow: '0 0 0 2px var(--color-primary-100)'
    }
  },
  versionSelect: {
    padding: '6px 10px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-sm)',
    outline: 'none',
    backgroundColor: 'white',
    color: 'var(--color-neutral-900)',
    cursor: 'pointer',
    minWidth: '240px',
    maxWidth: '360px',
    '&:focus': {
      borderColor: 'var(--color-primary-400)',
      boxShadow: '0 0 0 2px var(--color-primary-100)'
    }
  },
  tagBadgeWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginTop: '1px'
  },
  tagBadge: {
    display: 'inline-block',
    padding: '1px 6px',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: '10px',
    fontWeight: 600,
    fontFamily: 'var(--font-mono, monospace)',
    letterSpacing: '0.2px',
    backgroundColor: 'var(--color-neutral-100)',
    color: 'var(--color-neutral-500)'
  },
  tagBadgeLive: {
    backgroundColor: 'var(--color-success-050, #f0fdf4)',
    color: 'var(--color-success-700, #15803d)'
  },
  tagBadgeDraft: {
    backgroundColor: 'var(--color-warning-050, #fffbeb)',
    color: 'var(--color-warning-700, #b45309)'
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--gap-sm)',
    paddingBottom: '2px'
  },
  body: {
    display: 'flex',
    flex: 1,
    gap: 'var(--gap-lg)',
    padding: 'var(--padding-md) var(--padding-lg) 0',
    minHeight: 0,
    overflow: 'hidden'
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    border: '3px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-lg)',
    backgroundColor: 'white'
  },
  draftColumn: {
    flex: 1,
    minWidth: '420px'
  },
  draftColumnReadonly: {
    borderColor: 'var(--color-neutral-200)',
    backgroundColor: 'var(--color-neutral-050)'
  },
  agentColumn: {
    width: '380px',
    flexShrink: 0,
    backgroundColor: 'var(--color-ai-050)',
    borderColor: 'var(--color-ai-200, rgba(169,88,255,0.25))'
  },
  draftHeader: {
    padding: '8px 12px',
    borderBottom: '1px solid var(--color-neutral-200)',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: 'var(--text-size-sm)',
    fontWeight: 600,
    color: 'var(--color-neutral-700)',
    flexShrink: 0
  },
  draftStats: {
    fontSize: '11px',
    color: 'var(--color-neutral-500)',
    fontFamily: 'var(--font-mono, monospace)'
  },
  draftBody: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  },
  readonlyBanner: {
    backgroundColor: 'var(--color-success-050, #f0fdf4)',
    color: 'var(--color-success-800, #166534)',
    borderBottom: '1px solid var(--color-success-200, #bbf7d0)',
    padding: '8px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '12.5px'
  },
  readonlyDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: 'var(--color-success-500)',
    flexShrink: 0
  },
  emptyBlueprint: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '64px 32px',
    textAlign: 'center',
    color: 'var(--color-neutral-500)',
    gap: '16px'
  },
  emptyHeadline: {
    fontSize: '17px',
    fontWeight: 600,
    color: 'var(--color-neutral-700)'
  },
  emptyBody: {
    fontSize: '13px',
    lineHeight: 1.5,
    maxWidth: '440px',
    color: 'var(--color-neutral-500)'
  },
  statusBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--gap-lg)',
    padding: '5px var(--padding-lg)',
    flexShrink: 0,
    fontSize: '11px',
    color: 'var(--color-neutral-400)',
    borderTop: '1px solid var(--color-neutral-200)'
  },
  statusDot: {
    display: 'inline-block',
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    marginRight: '4px',
    verticalAlign: 'middle'
  }
}))

function PromoteConfirmModal ({ open, onClose, versionId, currentActive, onConfirm, processing }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Promote {versionId} to active?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Setting <strong>{versionId}</strong> as the active version will replace
          {currentActive ? <> the current active version <strong>{currentActive}</strong></> : ' the current active version'}.
          Anything consuming this blueprint will start using the new definition immediately.
        </DialogContentText>
        <DialogContentText style={{ marginTop: 12 }}>
          You can revert by promoting a previous version from the version selector.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <SydButton variant='ghost' size='md' onClick={onClose} disabled={processing}>
          Cancel
        </SydButton>
        <SydButton variant='primary' priority='warning' size='md' onClick={onConfirm} disabled={processing}>
          {processing ? 'Promoting\u2026' : 'Yes, make it active'}
        </SydButton>
      </DialogActions>
    </Dialog>
  )
}

PromoteConfirmModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  versionId: PropTypes.string,
  currentActive: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  processing: PropTypes.bool
}

function ForkConfirmModal ({ open, onClose, sourceVersionId, onConfirm, processing }) {
  const [description, setDescription] = useState('')

  useEffect(() => {
    if (open) setDescription('')
  }, [open])

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        {sourceVersionId
          ? <>Fork from <code>{sourceVersionId}</code> into a new draft</>
          : 'Create a new draft'}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          Creates a new non-active version copied from
          {sourceVersionId ? <> <strong>{sourceVersionId}</strong></> : ' the current active version'}
          . You'll be switched into the new draft to iterate. Promote it when you're ready
          to make it live.
        </DialogContentText>
        <SydLabel label='Description (optional)' helperText='What will change in this draft?'>
          <SydInput
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder='e.g. Add credit utilization metric'
          />
        </SydLabel>
      </DialogContent>
      <DialogActions>
        <SydButton variant='ghost' size='md' onClick={onClose} disabled={processing}>
          Cancel
        </SydButton>
        <SydButton variant='primary' size='md' onClick={() => onConfirm(description)} disabled={processing}>
          {processing ? 'Forking\u2026' : 'Create draft'}
        </SydButton>
      </DialogActions>
    </Dialog>
  )
}

ForkConfirmModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  sourceVersionId: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  processing: PropTypes.bool
}

function DeleteVersionConfirmModal ({ open, onClose, versionId, onConfirm, processing }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Delete draft {versionId}?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          This permanently removes the version <strong>{versionId}</strong> and any unsaved
          changes against it. This cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <SydButton variant='ghost' size='md' onClick={onClose} disabled={processing}>
          Cancel
        </SydButton>
        <SydButton variant='primary' priority='warning' size='md' onClick={onConfirm} disabled={processing}>
          {processing ? 'Deleting\u2026' : 'Delete'}
        </SydButton>
      </DialogActions>
    </Dialog>
  )
}

DeleteVersionConfirmModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  versionId: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  processing: PropTypes.bool
}

function DiscardSwitchConfirmModal ({ open, onClose, onConfirm }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Discard unsaved changes?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          You have unsaved changes in this draft. Switching will discard them. Save first
          if you want to keep them.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <SydButton variant='ghost' size='md' onClick={onClose}>Cancel</SydButton>
        <SydButton variant='primary' priority='warning' size='md' onClick={onConfirm}>
          Discard and switch
        </SydButton>
      </DialogActions>
    </Dialog>
  )
}

DiscardSwitchConfirmModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired
}

function ForkOnApplyConfirmModal ({ open, onClose, onConfirm, processing }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Fork the live version to apply this?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          You're viewing the live version, which can't be edited directly. To apply this
          proposal, we'll fork live into a new draft and apply the change there. You can
          promote the draft to live when you're ready.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <SydButton variant='ghost' size='md' onClick={onClose} disabled={processing}>Cancel</SydButton>
        <SydButton variant='primary' size='md' onClick={onConfirm} disabled={processing}>
          {processing ? 'Forking\u2026' : 'Fork and apply'}
        </SydButton>
      </DialogActions>
    </Dialog>
  )
}

ForkOnApplyConfirmModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  processing: PropTypes.bool
}

function StudioWorkspace ({
  blueprint,
  viewedVersionId,
  readOnly,
  onDirtyChange,
  agentSidePanelProps
}) {
  const classes = useStyles()
  const {
    draft,
    isDirty,
    appliedProposalIds,
    updatePurpose,
    updateOptionConfig,
    updateOptionField,
    removeOption,
    addPurpose,
    addOption
  } = useBlueprintStudio()
  const [previewOptionCode, setPreviewOptionCode] = useState(null)
  const [diagnostics, setDiagnostics] = useState(null)

  useEffect(() => {
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  const handleAnalyzeDql = useCallback((purposeCode, dql) => {
    setDiagnostics({ purposeCode, dql: dql || '' })
  }, [])

  const purposeCount = Object.keys(draft?.data?.purposes || {}).length
  const optionCount = Object.keys(draft?.presentation?.options || {}).length

  const handleDeletePurpose = useCallback((code) => updatePurpose(code, null), [updatePurpose])

  const cardCallbacks = readOnly
    ? {
      onUpdatePurpose: null,
      onDeletePurpose: null,
      onAddPurpose: null,
      onUpdateOptionConfig: null,
      onUpdateOptionField: null,
      onDeleteOption: null,
      onAddOption: null
    }
    : {
      onUpdatePurpose: updatePurpose,
      onDeletePurpose: handleDeletePurpose,
      onAddPurpose: addPurpose,
      onUpdateOptionConfig: updateOptionConfig,
      onUpdateOptionField: updateOptionField,
      onDeleteOption: removeOption,
      onAddOption: addOption
    }

  return (
    <>
      <div className={`${classes.column} ${classes.draftColumn} ${readOnly ? classes.draftColumnReadonly : ''}`}>
        {readOnly && (
          <div className={classes.readonlyBanner}>
            <span className={classes.readonlyDot} />
            <span>
              Viewing the <strong>live</strong> version <code>{viewedVersionId}</code>. Read-only — fork to make changes.
            </span>
          </div>
        )}
        <div className={classes.draftHeader}>
          <span>{readOnly ? 'Live definition' : 'Draft definition'}</span>
          <span className={classes.draftStats}>
            {purposeCount} purpose{purposeCount === 1 ? '' : 's'} • {optionCount} presentation option{optionCount === 1 ? '' : 's'}
          </span>
          <span style={{ flex: 1 }} />
          <span className={classes.draftStats}>
            {readOnly ? 'read-only' : (isDirty ? 'unsaved' : 'in sync with saved')}
          </span>
        </div>
        <div className={classes.draftBody}>
          <DraftBrowser
            draft={draft}
            onUpdatePurpose={cardCallbacks.onUpdatePurpose}
            onDeletePurpose={cardCallbacks.onDeletePurpose}
            onAddPurpose={cardCallbacks.onAddPurpose}
            onUpdateOptionConfig={cardCallbacks.onUpdateOptionConfig}
            onUpdateOptionField={cardCallbacks.onUpdateOptionField}
            onDeleteOption={cardCallbacks.onDeleteOption}
            onAddOption={cardCallbacks.onAddOption}
            onPreviewOption={setPreviewOptionCode}
            onAnalyzeDql={handleAnalyzeDql}
          />
        </div>
      </div>

      <div className={`${classes.column} ${classes.agentColumn}`}>
        <AgentSidePanel
          workspaceContext={{
            ...agentSidePanelProps.workspaceContext,
            draft,
            viewedVersionId,
            readOnly
          }}
          onApplyProposal={agentSidePanelProps.onApplyProposal}
          appliedProposalIds={appliedProposalIds}
        />
      </div>

      <PreviewModal
        open={!!previewOptionCode}
        onClose={() => setPreviewOptionCode(null)}
        draft={draft}
        optionCode={previewOptionCode}
        blueprintCode={blueprint.code}
        previewLabel={readOnly
          ? `Live (${viewedVersionId})`
          : (isDirty ? `Draft (${viewedVersionId}) · unsaved edits` : `Draft (${viewedVersionId})`)}
      />

      <DqlDiagnosticsModal
        open={!!diagnostics}
        onClose={() => setDiagnostics(null)}
        purposeCode={diagnostics?.purposeCode}
        savedDql={diagnostics?.dql}
      />
    </>
  )
}

StudioWorkspace.propTypes = {
  blueprint: PropTypes.object.isRequired,
  viewedVersionId: PropTypes.string,
  readOnly: PropTypes.bool,
  onDirtyChange: PropTypes.func.isRequired,
  agentSidePanelProps: PropTypes.object.isRequired
}

function StudioInner ({ blueprint, versions, refetchBlueprint, refetchVersions }) {
  const classes = useStyles()
  const history = useHistory()
  // Mirrors the provider's isDirty out so the host can guard version switches and navigation.
  const [isDirtyMirror, setIsDirtyMirror] = useState(false)
  const studioRef = useRef(null)
  const pendingProposalAfterMountRef = useRef(null)

  const activeVersionId = blueprint.definition?.definitionVersionId ?? null

  const sortedVersions = useMemo(() => {
    if (!versions) return []
    return [...versions].sort((a, b) =>
      new Date(b.versionDate || 0) - new Date(a.versionDate || 0)
    )
  }, [versions])

  const defaultViewedVersionId = useMemo(() => {
    if (activeVersionId) return activeVersionId
    return sortedVersions[0]?.definitionVersionId || null
  }, [activeVersionId, sortedVersions])

  const [viewedVersionId, setViewedVersionId] = useState(defaultViewedVersionId)

  // First-load reconciliation when versions arrive after initial render.
  useEffect(() => {
    if (!viewedVersionId && defaultViewedVersionId) {
      setViewedVersionId(defaultViewedVersionId)
    }
  }, [viewedVersionId, defaultViewedVersionId])

  const viewedVersion = useMemo(() => {
    return sortedVersions.find(v => v.definitionVersionId === viewedVersionId) || null
  }, [sortedVersions, viewedVersionId])

  const readOnly = !!(viewedVersionId && viewedVersionId === activeVersionId)
  const initialDraft = useMemo(() => ({
    data: viewedVersion?.data || { purposes: {} },
    presentation: viewedVersion?.presentation || { options: {} }
  }), [viewedVersion])

  // ---- mutations
  const { mutateAsync: addDefinition } = useAddQueryBlueprintDefinition()
  const { mutateAsync: modifyDefinition } = useModifyQueryBlueprintDefinition()
  const { mutateAsync: setActiveVersion } = useSetActiveQueryBlueprintVersion()
  const { mutateAsync: removeVersion } = useRemoveQueryBlueprintVersion()

  const [saving, setSaving] = useState(false)
  const [forking, setForking] = useState(false)
  const [promoting, setPromoting] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [toast, setToast] = useState(null)

  const [metadataModalOpen, setMetadataModalOpen] = useState(false)

  // Browser-level guard for tab close / external nav. <Prompt> covers in-app navigation.
  useEffect(() => {
    if (!isDirtyMirror) return undefined
    const handler = (event) => {
      event.preventDefault()
      event.returnValue = ''
      return ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isDirtyMirror])

  // Modal state
  const [forkOpen, setForkOpen] = useState(false)
  const [forkSourceVersionId, setForkSourceVersionId] = useState(null)
  const [forkOnApplyState, setForkOnApplyState] = useState(null)
  const [promoteOpen, setPromoteOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [discardSwitchTarget, setDiscardSwitchTarget] = useState(null)

  const performFork = useCallback(async (sourceVersion, description) => {
    if (!sourceVersion) throw new Error('Cannot fork: source version not found')
    setForking(true)
    try {
      const data = JSON.parse(JSON.stringify(sourceVersion.data || { purposes: {} }))
      const presentation = JSON.parse(JSON.stringify(sourceVersion.presentation || { options: {} }))

      const addResult = await addDefinition({
        blueprintId: blueprint.blueprintId,
        definition: {
          description: description || `Forked from ${sourceVersion.definitionVersionId}`,
          data,
          presentation
        },
        options: { setAsActive: false }
      })
      const newVersionId = addResult?.definitionVersionId
      if (!newVersionId) throw new Error('Fork failed: no version id returned')

      await Promise.all([refetchVersions(), refetchBlueprint()])
      setViewedVersionId(newVersionId)
      setIsDirtyMirror(false)
      return newVersionId
    } finally {
      setForking(false)
    }
  }, [addDefinition, blueprint.blueprintId, refetchBlueprint, refetchVersions])

  const handleFork = useCallback(async (description) => {
    const source = sortedVersions.find(v => v.definitionVersionId === forkSourceVersionId) ||
      sortedVersions.find(v => v.definitionVersionId === activeVersionId) ||
      sortedVersions[0]
    if (!source) {
      setToast({ message: 'No source version to fork from.', severity: 'error' })
      return
    }
    try {
      const newVersionId = await performFork(source, description)
      setForkOpen(false)
      setForkSourceVersionId(null)
      setToast({
        message: `Forked from ${source.definitionVersionId} → new draft ${newVersionId}.`,
        severity: 'success'
      })
    } catch (err) {
      setToast({ message: decipherError(err) || 'Fork failed', severity: 'error' })
    }
  }, [forkSourceVersionId, sortedVersions, activeVersionId, performFork])

  const handleCreateInitialDraft = useCallback(async () => {
    setForking(true)
    try {
      const result = await addDefinition({
        blueprintId: blueprint.blueprintId,
        definition: {
          description: 'Initial draft',
          data: { purposes: {} },
          presentation: { options: {} }
        },
        options: { setAsActive: false }
      })
      await Promise.all([refetchVersions(), refetchBlueprint()])
      const newVersionId = result?.definitionVersionId
      if (newVersionId) setViewedVersionId(newVersionId)
      setToast({ message: 'Created initial draft. Add purposes via the Designer or by hand.', severity: 'success' })
    } catch (err) {
      setToast({ message: decipherError(err) || 'Could not create initial draft', severity: 'error' })
    } finally {
      setForking(false)
    }
  }, [addDefinition, blueprint.blueprintId, refetchBlueprint, refetchVersions])

  const handleSave = useCallback(async () => {
    if (!studioRef.current) return
    if (readOnly) return
    const ctx = studioRef.current
    if (!ctx.isDirty) return
    setSaving(true)
    try {
      const data = JSON.parse(JSON.stringify(ctx.draft?.data || { purposes: {} }))
      const presentation = JSON.parse(JSON.stringify(ctx.draft?.presentation || { options: {} }))
      await modifyDefinition({
        blueprintId: blueprint.blueprintId,
        definitionVersionId: viewedVersionId,
        data,
        presentation
      })
      await refetchVersions()
      ctx.resetDraft({ data, presentation })
      setToast({ message: `Saved draft ${viewedVersionId}.`, severity: 'success' })
    } catch (err) {
      setToast({ message: decipherError(err) || 'Save failed', severity: 'error' })
    } finally {
      setSaving(false)
    }
  }, [readOnly, modifyDefinition, blueprint.blueprintId, viewedVersionId, refetchVersions])

  const handlePromote = useCallback(async () => {
    if (!viewedVersionId) return
    setPromoting(true)
    try {
      await setActiveVersion({
        blueprintId: blueprint.blueprintId,
        definitionVersionId: viewedVersionId
      })
      await Promise.all([refetchBlueprint(), refetchVersions()])
      setPromoteOpen(false)
      setToast({ message: `${viewedVersionId} is now the active version.`, severity: 'success' })
    } catch (err) {
      setToast({ message: decipherError(err) || 'Promote failed', severity: 'error' })
    } finally {
      setPromoting(false)
    }
  }, [viewedVersionId, setActiveVersion, blueprint.blueprintId, refetchBlueprint, refetchVersions])

  const handleDiscardLocal = useCallback(() => {
    if (!studioRef.current) return
    studioRef.current.resetDraft({
      data: viewedVersion?.data || { purposes: {} },
      presentation: viewedVersion?.presentation || { options: {} }
    })
    setToast({ message: 'Discarded local edits.', severity: 'info' })
  }, [viewedVersion])

  const handleDeleteDraft = useCallback(async () => {
    if (!viewedVersionId) return
    if (viewedVersionId === activeVersionId) {
      setToast({ message: 'Cannot delete the active version.', severity: 'error' })
      return
    }
    setDeleting(true)
    try {
      const deletedId = viewedVersionId
      await removeVersion({
        blueprintId: blueprint.blueprintId,
        definitionVersionId: deletedId
      })
      await Promise.all([refetchVersions(), refetchBlueprint()])
      // Explicit switch — relying on a watcher would race with fork / manual switch calls.
      setViewedVersionId(activeVersionId || null)
      setDeleteOpen(false)
      setToast({ message: `Deleted draft ${deletedId}.`, severity: 'success' })
    } catch (err) {
      setToast({ message: decipherError(err) || 'Delete failed', severity: 'error' })
    } finally {
      setDeleting(false)
    }
  }, [viewedVersionId, activeVersionId, removeVersion, blueprint.blueprintId, refetchBlueprint, refetchVersions])

  const handleSwitchVersion = useCallback((nextVersionId) => {
    if (nextVersionId === viewedVersionId) return
    if (isDirtyMirror) {
      setDiscardSwitchTarget(nextVersionId)
      return
    }
    setViewedVersionId(nextVersionId)
  }, [viewedVersionId, isDirtyMirror])

  const confirmDiscardAndSwitch = useCallback(() => {
    if (discardSwitchTarget) setViewedVersionId(discardSwitchTarget)
    setIsDirtyMirror(false)
    setDiscardSwitchTarget(null)
  }, [discardSwitchTarget])

  // ---- agent fork-on-apply
  const handleApplyProposal = useCallback((change, proposalId) => {
    if (!studioRef.current) return
    if (readOnly) {
      setForkOnApplyState({ change, proposalId })
      return
    }
    studioRef.current.applyProposal(change, proposalId)
  }, [readOnly])

  const consumePendingProposal = useCallback(() => {
    const p = pendingProposalAfterMountRef.current
    pendingProposalAfterMountRef.current = null
    return p
  }, [])

  const confirmForkOnApply = useCallback(async () => {
    const stash = forkOnApplyState
    if (!stash) return
    const source = sortedVersions.find(v => v.definitionVersionId === activeVersionId) ||
      sortedVersions[0]
    if (!source) return
    try {
      // Stash the proposal BEFORE performFork triggers the provider remount.
      pendingProposalAfterMountRef.current = stash
      await performFork(source, `Forked from ${source.definitionVersionId} via agent proposal`)
      setForkOnApplyState(null)
    } catch (err) {
      pendingProposalAfterMountRef.current = null
      setForkOnApplyState(null)
      setToast({ message: decipherError(err) || 'Fork failed', severity: 'error' })
    }
  }, [forkOnApplyState, sortedVersions, activeVersionId, performFork])

  const versionOptions = useMemo(() => {
    return sortedVersions.map(v => {
      const isActive = v.definitionVersionId === activeVersionId
      const date = v.versionDate
        ? new Date(v.versionDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
        : null
      const desc = v.description?.trim() || 'Untitled draft'
      const parts = []
      if (date) parts.push(date)
      parts.push(desc)
      if (isActive) parts.push('live')
      return {
        label: parts.join(' · '),
        value: v.definitionVersionId
      }
    })
  }, [sortedVersions, activeVersionId])

  // Static identity here; live draft / version / applied IDs are merged inside the provider.
  const agentSidePanelProps = useMemo(() => ({
    workspaceContext: {
      blueprintId: blueprint.blueprintId,
      blueprintCode: blueprint.code,
      displayName: blueprint.name
    },
    onApplyProposal: handleApplyProposal
  }), [blueprint.blueprintId, blueprint.code, blueprint.name, handleApplyProposal])

  const noVersionsAtAll = !sortedVersions.length
  const noActive = !activeVersionId

  return (
    <div className={classes.page}>
      <Prompt
        when={isDirtyMirror}
        message={() => 'You have unsaved changes in the draft. Are you sure you want to leave?'}
      />

      <div className={classes.headerBar}>
        <div className={classes.headerField} style={{ width: '160px' }}>
          <label className={classes.fieldLabel}>Code</label>
          <input
            className={classes.fieldInput}
            value={blueprint.code || ''}
            readOnly
            style={{ backgroundColor: 'var(--color-neutral-050)', cursor: 'default', fontFamily: 'var(--font-mono, monospace)' }}
            title='Blueprint code is immutable after creation.'
          />
        </div>
        <div className={classes.headerField} style={{ width: '220px' }}>
          <label className={classes.fieldLabel}>Name</label>
          <input
            className={classes.fieldInput}
            value={blueprint.name || ''}
            readOnly
            style={{ backgroundColor: 'var(--color-neutral-050)', cursor: 'default' }}
            title={blueprint.description || 'Click "Edit details" to change blueprint metadata.'}
          />
        </div>

        {!noVersionsAtAll && (
          <div className={classes.headerField}>
            <label className={classes.fieldLabel}>Version</label>
            <select
              className={classes.versionSelect}
              value={viewedVersionId || ''}
              onChange={(e) => handleSwitchVersion(e.target.value)}
            >
              {versionOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        )}

        <div className={classes.headerField} style={{ marginLeft: 'auto' }}>
          {!noVersionsAtAll && (
            <div className={classes.tagBadgeWrap}>
              <span className={`${classes.tagBadge} ${readOnly ? classes.tagBadgeLive : classes.tagBadgeDraft}`}>
                {readOnly ? 'live' : 'draft'}
              </span>
              {!readOnly && isDirtyMirror && (
                <span className={classes.tagBadge}>unsaved</span>
              )}
            </div>
          )}
          <div className={classes.headerActions}>
            {!noVersionsAtAll && (readOnly
              ? (
                <SydButton
                  size='sm'
                  variant='primary'
                  onClick={() => { setForkSourceVersionId(viewedVersionId); setForkOpen(true) }}
                  disabled={forking}
                >
                  {forking ? 'Forking\u2026' : 'Fork to draft'}
                </SydButton>
              )
              : (
                <DraftActions
                  viewedVersionId={viewedVersionId}
                  activeVersionId={activeVersionId}
                  isDirty={isDirtyMirror}
                  onSave={handleSave}
                  onPromote={() => setPromoteOpen(true)}
                  onDiscardLocal={handleDiscardLocal}
                  onDelete={() => setDeleteOpen(true)}
                  onFork={() => { setForkSourceVersionId(viewedVersionId); setForkOpen(true) }}
                  saving={saving}
                  promoting={promoting}
                  deleting={deleting}
                />
              ))}
            <SydButton
              size='sm'
              variant='ghost'
              icon='edit'
              onClick={() => setMetadataModalOpen(true)}
              title='Edit blueprint name, description, and enabled status'
            >
              Edit details
            </SydButton>
            <SydButton
              size='sm'
              variant='ghost'
              onClick={() => history.push(`/admin/denali/analysis/query-blueprints/${blueprint.blueprintId}`)}
            >
              Back to Editor
            </SydButton>
          </div>
        </div>
      </div>

      <div className={classes.body}>
        {noVersionsAtAll ? (
          <div className={`${classes.column} ${classes.draftColumn}`}>
            <div className={classes.emptyBlueprint}>
              <div className={classes.emptyHeadline}>This blueprint has no definitions yet.</div>
              <div className={classes.emptyBody}>
                Create the first draft to start defining purposes and presentations. Once you're
                ready, promote it to make it live.
              </div>
              <SydButton
                size='md'
                variant='primary'
                onClick={handleCreateInitialDraft}
                disabled={forking}
              >
                {forking ? 'Creating\u2026' : 'Create first draft'}
              </SydButton>
            </div>
          </div>
        ) : (
          <BlueprintStudioProvider
            key={viewedVersionId}
            initialDraft={initialDraft}
            blueprintId={blueprint.blueprintId}
            blueprintCode={blueprint.code}
          >
            <ProviderBridge
              studioRef={studioRef}
              consumePendingProposal={consumePendingProposal}
            />
            <StudioWorkspace
              blueprint={blueprint}
              viewedVersionId={viewedVersionId}
              readOnly={readOnly}
              onDirtyChange={setIsDirtyMirror}
              agentSidePanelProps={agentSidePanelProps}
            />
          </BlueprintStudioProvider>
        )}
      </div>

      <div className={classes.statusBar}>
        <span>
          <span
            className={classes.statusDot}
            style={{ backgroundColor: readOnly ? 'var(--color-success-500)' : (isDirtyMirror ? 'var(--color-warning-400)' : 'var(--color-success-500)') }}
          />
          {readOnly ? 'Live · read-only' : (isDirtyMirror ? 'Unsaved draft changes' : 'Draft saved')}
        </span>
        {noActive && !noVersionsAtAll && (
          <span style={{ color: 'var(--color-warning-700)' }}>
            No active version — promote a draft to make it live
          </span>
        )}
        <span style={{ marginLeft: 'auto' }}>Blueprint Studio (preview)</span>
      </div>

      <BlueprintMetadataModal
        open={metadataModalOpen}
        onClose={() => setMetadataModalOpen(false)}
        blueprint={blueprint}
        onSaved={refetchBlueprint}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={toast?.action ? 8000 : 3500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {toast && (
          <Alert
            onClose={() => setToast(null)}
            severity={toast.severity}
            variant='filled'
            elevation={6}
            action={toast.action ? (
              <SydButton
                size='xs'
                variant='secondary'
                onClick={() => {
                  toast.action.onClick()
                  setToast(null)
                }}
              >
                {toast.action.label}
              </SydButton>
            ) : undefined}
          >
            {toast.message}
          </Alert>
        )}
      </Snackbar>

      <PromoteConfirmModal
        open={promoteOpen}
        onClose={() => setPromoteOpen(false)}
        versionId={viewedVersionId}
        currentActive={activeVersionId}
        onConfirm={handlePromote}
        processing={promoting}
      />
      <ForkConfirmModal
        open={forkOpen}
        onClose={() => { setForkOpen(false); setForkSourceVersionId(null) }}
        sourceVersionId={forkSourceVersionId}
        onConfirm={handleFork}
        processing={forking}
      />
      <DeleteVersionConfirmModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        versionId={viewedVersionId}
        onConfirm={handleDeleteDraft}
        processing={deleting}
      />
      <DiscardSwitchConfirmModal
        open={!!discardSwitchTarget}
        onClose={() => setDiscardSwitchTarget(null)}
        onConfirm={confirmDiscardAndSwitch}
      />
      <ForkOnApplyConfirmModal
        open={!!forkOnApplyState}
        onClose={() => setForkOnApplyState(null)}
        onConfirm={confirmForkOnApply}
        processing={forking}
      />
    </div>
  )
}

StudioInner.propTypes = {
  blueprint: PropTypes.object.isRequired,
  versions: PropTypes.array,
  refetchBlueprint: PropTypes.func.isRequired,
  refetchVersions: PropTypes.func.isRequired
}

function DraftActions ({
  viewedVersionId,
  activeVersionId,
  isDirty,
  onSave,
  onPromote,
  onDiscardLocal,
  onDelete,
  onFork,
  saving,
  promoting,
  deleting
}) {
  const canPromote = viewedVersionId && viewedVersionId !== activeVersionId
  const canDelete = viewedVersionId && viewedVersionId !== activeVersionId
  return (
    <>
      <SydButton size='sm' variant='ghost' onClick={onFork} disabled={saving || promoting || deleting}>
        Fork
      </SydButton>
      <SydButton size='sm' variant='secondary' onClick={onDiscardLocal} disabled={!isDirty || saving || promoting}>
        Reset
      </SydButton>
      <SydButton
        size='sm'
        variant='primary'
        icon='check'
        onClick={onSave}
        disabled={!isDirty || saving || promoting}
      >
        {saving ? 'Saving\u2026' : 'Save'}
      </SydButton>
      {canPromote && (
        <SydButton
          size='sm'
          variant='primary'
          priority='warning'
          onClick={onPromote}
          disabled={saving || promoting || isDirty}
          title={isDirty ? 'Save first to enable promote' : 'Promote this draft to active'}
        >
          {promoting ? 'Promoting\u2026' : 'Promote'}
        </SydButton>
      )}
      {canDelete && (
        <SydButton size='sm' variant='ghost' onClick={onDelete} disabled={saving || promoting || deleting}>
          {deleting ? 'Deleting\u2026' : 'Delete'}
        </SydButton>
      )}
    </>
  )
}

DraftActions.propTypes = {
  viewedVersionId: PropTypes.string,
  activeVersionId: PropTypes.string,
  isDirty: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
  onPromote: PropTypes.func.isRequired,
  onDiscardLocal: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onFork: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  promoting: PropTypes.bool,
  deleting: PropTypes.bool
}

/**
 * Exposes the inner studio context through a ref so the host can drive
 * save/discard/applyProposal, and consumes any proposal stashed during a
 * fork-on-apply flow.
 */
function ProviderBridge ({ studioRef, consumePendingProposal }) {
  const ctx = useBlueprintStudio()
  studioRef.current = ctx

  useEffect(() => {
    const pending = consumePendingProposal()
    if (pending && pending.change) {
      ctx.applyProposal(pending.change, pending.proposalId)
    }
    // Only consume on mount of a fresh provider key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return null
}

ProviderBridge.propTypes = {
  studioRef: PropTypes.object.isRequired,
  consumePendingProposal: PropTypes.func.isRequired
}

function BlueprintStudio () {
  const { blueprintId } = useParams()
  const id = parseInt(blueprintId, 10)
  const {
    data: blueprint,
    isLoading: blueprintLoading,
    error: blueprintError,
    refetch: refetchBlueprint
  } = useGetQueryBlueprint(id, { includes: { definition: true } })

  const {
    data: versions,
    isLoading: versionsLoading,
    refetch: refetchVersions
  } = useListQueryBlueprintVersions(id)

  if (blueprintError) return <ErrorDisplay error={blueprintError} onRetry={refetchBlueprint} />
  if (blueprintLoading || !blueprint) return <Loading />
  if (versionsLoading && !versions) return <Loading />

  return (
    <StudioInner
      blueprint={blueprint}
      versions={versions || []}
      refetchBlueprint={refetchBlueprint}
      refetchVersions={refetchVersions}
    />
  )
}

export default BlueprintStudio
